import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo.svg";
import { useAuthStore } from "../stores/adminStore";
import { Eye, EyeOff } from "lucide-react";

const Login = () => {
  const [rememberMe, setRememberMe] = useState(false);
  const [authData, setAuthData] = useState({
    loginId: "",
    password: "",
  });
  const [passwordVisible, setPasswordVisible] = useState(false);

  const { loginId, password } = authData;
  const { authLogin, auth, error, setError } = useAuthStore();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAuthData({
      ...authData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors

    try {
      await authLogin({ loginId, password });
      console.log("Login.js - After authLogin, auth:", auth());
    } catch (error) {
      setError(error.response?.data?.message || "Login failed");
    }
  };

  // Navigate to root route when auth state updates
  useEffect(() => {
    const isLoggedIn = auth();
    if (isLoggedIn) {
      console.log("Login.js - Navigating to /, auth:", isLoggedIn);
      navigate("/", { replace: true });
    }
  }, [auth, navigate]); // Depend on auth function reference

  return (
    <div className="flex h-screen w-full">
      {/* Left side - Map background with text */}
      <div className="hidden md:flex md:w-2/3 bg-login-bg bg-cover bg-center relative">
        <div className="absolute inset-0 opacity-50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-8">
          <h1 className="text-5xl font-bold mb-4">RCAATS</h1>
          <p className="text-4xl max-w-2xl">
            Ras Al Khaimah aims to enhance the management of road infrastructure
            assets
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full md:w-1/3 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Logo" className="w-24 h-24" />
          </div>

          <div className="bg-white p-8 rounded-lg shadow-2xl border border-gray-100">
            <h2 className="text-2xl font-semibold text-center mb-2">Sign In</h2>
            <p className="text-gray-500 text-center mb-6">
              Welcome back! Please enter your details
            </p>

            {error && (
              <div className="mb-4 text-red-500 text-center">{error}</div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label
                  htmlFor="email/username"
                  className="block text-gray-600 mb-1"
                >
                  Email/Username
                </label>
                <input
                  id="email/username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter email"
                  name="loginId"
                  value={loginId}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-4 relative">
                <label htmlFor="password" className="block text-gray-600 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type={passwordVisible ? "text" : "password"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 pr-10"
                  placeholder="Enter password"
                  name="password"
                  value={password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  className="absolute inset-y-0 right-0 pr-3 pt-7 flex items-center cursor-pointer"
                >
                  {passwordVisible ? (
                    <EyeOff className="w-5 h-5 text-gray-500" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-500" />
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-600"
                  >
                    Remember me
                  </label>
                </div>
                <a
                  href="#"
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Forgot your password?
                </a>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-900 text-white py-2 px-4 rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                SIGN IN
              </button>
            </form>
          </div>

          <div className="text-center mt-6 text-sm text-gray-600">
            2025 RCAATS All Rights Reserved
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;